//* Libraries Import
import { useLocation } from 'react-router-dom';
import { useMemo } from 'react';

//* Schemas Import
import type { FullProcess } from '../schemas/common/process/process';

//* Constants Import
import { ROUTE_PERMISSION_MAP } from '../constants/permissions';

export interface PermissionCheckResult {
  hasAccess: boolean;
  isProcessView: boolean;
  requiredPermission?: string;
  errorMessage?: string;
  debugInfo?: {
    currentPath: string;
    routeSuffix: string;
    permissionRoute: string;
    foundConfig: boolean;
  };
}

/**
 * Extracts route suffix from full path for permission checking
 * @param fullPath - Complete URL path
 * @param processId - Current process ID
 * @returns Route suffix after process ID
 */
const extractRouteSuffix = (fullPath: string, processId: number): string => {
  const processBasePath = `/process/${processId}`;
  return fullPath.replace(processBasePath, '');
};

/**
 * Normalizes route for permission checking
 * Handles special cases like results routes that inherit parent permissions
 * @param routeSuffix - Route suffix after process ID
 * @returns Normalized route for permission lookup
 */
const normalizeRouteForPermissions = (routeSuffix: string): string => {
  // Handle results routes - they inherit parent permission
  if (routeSuffix.includes('/results/')) {
    const parentRoute = routeSuffix.split('/results/')[0];
    return parentRoute;
  }
  
  // Handle edit routes - they inherit parent permission
  if (routeSuffix.includes('/edit/')) {
    const parentRoute = routeSuffix.split('/edit/')[0];
    return parentRoute;
  }
  
  return routeSuffix;
};

/**
 * Checks if user has specific feature permission
 * @param permissions - Faculty permissions array
 * @param featureKey - Feature key to check
 * @returns Boolean indicating if permission exists
 */
const hasFeaturePermission = (
  permissions: NonNullable<FullProcess['faculdadePermissions']>,
  featureKey: string
): boolean => {
  return permissions.some(permission => {
    if (!permission.enabled) return false;
    
    return permission.features.some(feature => 
      feature.feature_key === featureKey && feature.permissionEnabled
    );
  });
};

/**
 * Checks if user has specific subFeature permission
 * @param permissions - Faculty permissions array
 * @param subFeatureKey - SubFeature key to check
 * @returns Boolean indicating if permission exists
 */
const hasSubFeaturePermission = (
  permissions: NonNullable<FullProcess['faculdadePermissions']>,
  subFeatureKey: string
): boolean => {
  return permissions.some(permission => {
    if (!permission.enabled) return false;
    
    return permission.features.some(feature => {
      if (!feature.permissionEnabled) return false;
      
      return feature.subFeatures.some(subFeature =>
        subFeature.subFeatureKey === subFeatureKey && subFeature.permissionEnabled
      );
    });
  });
};

/**
 * Custom hook to check if user has permission to access current route
 * Provides comprehensive permission checking with debug information
 * 
 * @param processId - The current process ID
 * @param faculdadePermissions - The user's permissions for this process
 * @returns Object with access permission and error details
 */
export function usePermissionChecker(
  processId: number,
  faculdadePermissions?: FullProcess['faculdadePermissions']
): PermissionCheckResult {
  const location = useLocation();

  const permissionCheck = useMemo((): PermissionCheckResult => {
    const currentPath = location.pathname;
    const processBasePath = `/process/${processId}`;
    
    // Check if we're on the main process view page - always allowed
    if (currentPath === processBasePath) {
      return {
        hasAccess: true,
        isProcessView: true,
        debugInfo: {
          currentPath,
          routeSuffix: '',
          permissionRoute: '',
          foundConfig: true
        }
      };
    }

    // If no permissions data available, deny access to subpages
    if (!faculdadePermissions) {
      return {
        hasAccess: false,
        isProcessView: false,
        errorMessage: 'Permissões não carregadas. Tente novamente.',
        debugInfo: {
          currentPath,
          routeSuffix: '',
          permissionRoute: '',
          foundConfig: false
        }
      };
    }

    // Extract and normalize route for permission checking
    const routeSuffix = extractRouteSuffix(currentPath, processId);
    const permissionRoute = normalizeRouteForPermissions(routeSuffix);
    const routeConfig = ROUTE_PERMISSION_MAP[permissionRoute];
    
    const debugInfo = {
      currentPath,
      routeSuffix,
      permissionRoute,
      foundConfig: Boolean(routeConfig)
    };

    // Route not mapped in permission system
    if (!routeConfig) {
      return {
        hasAccess: false,
        isProcessView: false,
        errorMessage: 'Rota não reconhecida no sistema de permissões.',
        debugInfo
      };
    }

    // Check feature permissions
    if (routeConfig.featureKey) {
      const hasPermission = hasFeaturePermission(faculdadePermissions, routeConfig.featureKey);
      
      if (hasPermission) {
        return {
          hasAccess: true,
          isProcessView: false,
          requiredPermission: routeConfig.featureKey,
          debugInfo
        };
      }
    }

    // Check subFeature permissions
    if (routeConfig.subFeatureKey) {
      const hasPermission = hasSubFeaturePermission(faculdadePermissions, routeConfig.subFeatureKey);
      
      if (hasPermission) {
        return {
          hasAccess: true,
          isProcessView: false,
          requiredPermission: routeConfig.subFeatureKey,
          debugInfo
        };
      }
    }

    // No permission found
    return {
      hasAccess: false,
      isProcessView: false,
      requiredPermission: routeConfig.featureKey || routeConfig.subFeatureKey,
      errorMessage: 'Você não tem permissão para acessar esta funcionalidade.',
      debugInfo
    };

  }, [location.pathname, processId, faculdadePermissions]);

  return permissionCheck;
}
